import { CURSOR_RECENT_MESSAGE } from '@/graphql/cursor.graphql';
import {
  CallStatus,
  ConversationCombinations,
  ConversationDirection,
  ConversationType,
  MessageStatus,
} from '@/types/app.types';
import {
  DirectionTypeEnum,
  MessageConversationTypeEnum,
  RecentMessageConversationsV2Query,
  RecentMessageConversationsV2QueryVariables,
  SortEnum,
  VerifyAndTransitionModelEnum,
} from '@/types/generated/graphql';
import { HANG_CALL_STATUS } from '@modules/phone/constants/transition-call-status.constants';
import { useTransitionCallStatus } from '@modules/phone/features/in-call/hooks/in-call-body/use-transition-call-status';
import { CALL_DIRECTION, CALL_STATUS } from '@shared/constants';
import { useCursorPagination } from '@shared/hooks/use-cursor-pagination';
import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';
import { phoneParser } from '@shared/utils';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
function parseFilters(conversationType: string) {
  const variables: Record<PropertyKey, string | string[] | boolean> = {};
  switch (conversationType) {
    case CALL_DIRECTION.INCOMING: {
      variables.messageDirection = CALL_DIRECTION.INCOMING.toLowerCase() as DirectionTypeEnum.incoming;
      variables.conversationStatus = [CALL_STATUS.INITIATED, CALL_STATUS.COMPLETED];
      break;
    }

    case CALL_DIRECTION.OUTGOING: {
      variables.messageDirection = CALL_DIRECTION.OUTGOING.toLowerCase() as DirectionTypeEnum.outgoing;
      break;
    }

    case 'missed': {
      variables.conversationStatus = [CALL_STATUS.BUSY, CALL_STATUS.CANCELED, CALL_STATUS.NO_ANSWER];
      break;
    }

    case 'voicemail': {
      variables.voicemail = true;
      break;
    }
    case 'schedule': {
      variables.scheduleMessage = true;
    }
  }

  return variables;
}

type RecentMessageOptions = {
  query?: string;
};

export function useRecentMessage(options?: RecentMessageOptions) {
  const { channelId = 0 } = useParams();
  const [params] = useEncryptedParams();
  const query = options?.query ?? params.get('query') ?? '';
  const markAsClosed = params.get('markAsClosed');
  const conversationType = params.get('conversationType') ?? '';

  const { data, loading, loadNextRef, initialLoading, hasNextPage, refetch } = useCursorPagination<
    RecentMessageConversationsV2Query,
    RecentMessageConversationsV2QueryVariables
  >({
    schema: CURSOR_RECENT_MESSAGE,
    responseDataKey: 'recentMessageConversationsV2',
    variables: {
      sort: SortEnum.DESC,
      channelId: Number(channelId),
      markAsClosed: markAsClosed === 'true',
      ...parseFilters(conversationType),
      query,
    },
  });
  const { transitionStatusHelper, maxCallDuration } = useTransitionCallStatus();

  const conversations = useMemo(() => {
    const hangMessageIds: number[] = [];

    const messages = data?.data?.map((messageInfo) => {
      const { client, updatedAt, messageConversation, id, unreadCounter } = messageInfo || {};
      const clientName = isNaN(parseInt(String(client?.name)))
        ? String(client?.name)
        : phoneParser.format(client?.phoneNumber);

      // update messageIds to the server when call status is hanging
      if (
        HANG_CALL_STATUS.includes(messageConversation?.conversationStatus) &&
        dayjs().diff(updatedAt, 'seconds') >= maxCallDuration &&
        messageConversation?.conversationType === MessageConversationTypeEnum.conference
      ) {
        hangMessageIds.push(Number(id));
      }

      return {
        id: Number(id),
        avatarUrl: client?.profilePicture?.url ?? client?.country?.flag,
        name: clientName,
        date: updatedAt,
        pinned: !!messageInfo?.pinned,
        messageConversation: {
          numMedia: messageConversation?.numMedia ?? 0,
          direction: messageConversation?.direction as ConversationDirection,
          type: messageConversation?.conversationType as ConversationType,
          status: messageConversation?.conversationStatus as MessageStatus | CallStatus,
          messageContent: messageConversation?.messageContent || '',
          voicemailUrl: messageConversation?.voicemailRecordingUrl ?? '',
          conversationCombinations: (messageConversation?.direction +
            '-' +
            messageConversation?.conversationType +
            '-' +
            messageConversation?.conversationStatus) as ConversationCombinations,
        },
        client: {
          id: client?.id ?? 0,
          phoneNumber: String(client?.phoneNumber),
          isBlocked: client?.isBlocked ?? false,
        },
        unreadCounter: unreadCounter ?? 0,
        lastReadCursor: messageInfo?.lastReadCursor,
        draft: {
          mediaCount: messageInfo?.draft?.mediaCount ?? 0,
          messageContent: messageInfo?.draft?.messageContent ?? '',
        },
        hasScheduledMessages: !!messageInfo?.hasScheduledMessages,
        forwardedTo: !!messageInfo?.messageConversation?.forwardedTo?.externalNumber,
      };
    });

    transitionStatusHelper(hangMessageIds, VerifyAndTransitionModelEnum.RecentMessageConversation);

    return messages;
  }, [data]);

  useEffect(() => {
    refetch({
      sort: SortEnum.DESC,
      channelId: Number(channelId),
      markAsClosed: markAsClosed === 'true',
      query,
    });
  }, [markAsClosed]);

  useEffect(() => {
    refetch({
      sort: SortEnum.DESC,
      channelId: Number(channelId),
      ...parseFilters(conversationType),
      query,
    });
  }, [conversationType]);

  return {
    data: conversations,
    hasNextPage,
    loading,
    loadNextRef,
    initialLoading,
  };
}
